/**
 * CORE-1682 — вложения в чатах мастера: правила, проверка и слова для человека.
 *
 * Сервер (CORE-1703) отдаёт политику канала — `GET /api/chat/attachments/policy
 * ?channelKey=` — и сам проверяет формат по содержимому и размер. Здесь то, что
 * нужно до запроса: строка `accept` для выбора файла, проверка на клиенте с
 * тем же ответом, что даст сервер, подпись с лимитами и перевод кодов отказа.
 * Без React и RN — проверяется под jest.
 */

export interface ChatAttachmentFormat {
  /** Без точки, в нижнем регистре: `pdf`, `jpg`. */
  extension: string;
  mimeType: string;
  kind: 'image' | 'file';
}

export interface ChatAttachmentPolicy {
  enabled: boolean;
  /** Может ли текущий пользователь прикреплять в этот канал. */
  canAttach: boolean;
  maxAttachmentsPerMessage: number;
  maxImageSizeBytes: number;
  maxDocumentSizeBytes: number;
  formats: ChatAttachmentFormat[];
}

/** Что сервер отдаёт на загрузку: ссылки нет, до отправки файл никому не виден. */
export interface CommunityAttachmentUpload {
  id: string;
  type: 'image' | 'file';
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
}

const MB = 1024 * 1024;

/**
 * Запасная политика — та, что в ТЗ и у сервера по умолчанию. Берётся только
 * когда политику не удалось прочитать: без неё композер остался бы без скрепки.
 * Сервер проверит сам, так что расхождение стоит одной лишней ошибки, не дыры.
 */
export const DEFAULT_COMMUNITY_ATTACHMENT_POLICY: ChatAttachmentPolicy = {
  enabled: true,
  canAttach: true,
  maxAttachmentsPerMessage: 10,
  maxImageSizeBytes: 25 * MB,
  maxDocumentSizeBytes: 25 * MB,
  formats: [
    { extension: 'pdf', mimeType: 'application/pdf', kind: 'file' },
    { extension: 'doc', mimeType: 'application/msword', kind: 'file' },
    { extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', kind: 'file' },
    { extension: 'xls', mimeType: 'application/vnd.ms-excel', kind: 'file' },
    { extension: 'xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', kind: 'file' },
    { extension: 'ppt', mimeType: 'application/vnd.ms-powerpoint', kind: 'file' },
    { extension: 'pptx', mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', kind: 'file' },
    { extension: 'txt', mimeType: 'text/plain', kind: 'file' },
    { extension: 'csv', mimeType: 'text/csv', kind: 'file' },
    { extension: 'png', mimeType: 'image/png', kind: 'image' },
    { extension: 'jpg', mimeType: 'image/jpeg', kind: 'image' },
    { extension: 'jpeg', mimeType: 'image/jpeg', kind: 'image' },
    { extension: 'webp', mimeType: 'image/webp', kind: 'image' },
    { extension: 'gif', mimeType: 'image/gif', kind: 'image' },
  ],
};

/** Ответ сервера (camel или Pascal) → политика; `null`, если это не она. */
export function parseAttachmentPolicy(raw: any): ChatAttachmentPolicy | null {
  if (!raw || typeof raw !== 'object') return null;
  const pick = (c: string, p: string) => (raw[c] !== undefined ? raw[c] : raw[p]);
  const formatsRaw = pick('formats', 'Formats');
  if (!Array.isArray(formatsRaw)) return null;
  const formats: ChatAttachmentFormat[] = formatsRaw
    .map((f: any) => ({
      extension: String(f?.extension ?? f?.Extension ?? '').replace(/^\./, '').toLowerCase(),
      mimeType: String(f?.mimeType ?? f?.MimeType ?? '').toLowerCase(),
      kind: (f?.kind ?? f?.Kind) === 'image' ? 'image' as const : 'file' as const,
    }))
    .filter((f) => f.extension);
  return {
    enabled: pick('enabled', 'Enabled') !== false,
    canAttach: pick('canAttach', 'CanAttach') !== false,
    maxAttachmentsPerMessage: Number(pick('maxAttachmentsPerMessage', 'MaxAttachmentsPerMessage')) || 10,
    maxImageSizeBytes: Number(pick('maxImageSizeBytes', 'MaxImageSizeBytes')) || 25 * MB,
    maxDocumentSizeBytes: Number(pick('maxDocumentSizeBytes', 'MaxDocumentSizeBytes')) || 25 * MB,
    formats,
  };
}

/** «1,2 МБ», «340 КБ», «12 Б». */
export function formatBytesRu(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} Б`;
  if (bytes < MB) return `${Math.round(bytes / 1024)} КБ`;
  const mb = bytes / MB;
  const text = mb >= 10 ? String(Math.round(mb)) : (Math.round(mb * 10) / 10).toString().replace('.', ',');
  return `${text} МБ`;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

/** Строка `accept` для выбора файла: расширения и типы из политики. */
export function acceptFromPolicy(policy: ChatAttachmentPolicy): string {
  const parts = new Set<string>();
  policy.formats.forEach((f) => {
    parts.add(`.${f.extension}`);
    if (f.mimeType) parts.add(f.mimeType);
  });
  return [...parts].join(',');
}

/** Расширения заглавными через запятую: «PDF, DOC, DOCX, …». */
export function listFormats(policy: ChatAttachmentPolicy): string {
  return policy.formats.map((f) => f.extension.toUpperCase()).join(', ');
}

/** Подпись с лимитами — её человек видит у скрепки, а не узнаёт из отказа. */
export function describePolicy(policy: ChatAttachmentPolicy): string {
  const same = policy.maxImageSizeBytes === policy.maxDocumentSizeBytes;
  const size = same
    ? `до ${formatBytesRu(policy.maxImageSizeBytes)} каждый`
    : `картинки до ${formatBytesRu(policy.maxImageSizeBytes)}, документы до ${formatBytesRu(policy.maxDocumentSizeBytes)}`;
  return `До ${policy.maxAttachmentsPerMessage} файлов в сообщении, ${size}. ${listFormats(policy)}.`;
}

/**
 * Проверка до загрузки — те же правила, что у сервера, ответ по-русски.
 * `null` — файл подходит.
 */
export function validateAgainstPolicy(
  file: { name: string; type?: string; size: number },
  policy: ChatAttachmentPolicy,
): string | null {
  const name = file.name || 'файл';
  const ext = extensionOf(name);
  const mime = (file.type || '').toLowerCase();
  const format = policy.formats.find((f) => f.extension === ext)
    ?? (mime ? policy.formats.find((f) => f.mimeType === mime) : undefined);
  if (!format) {
    return `Файл «${name}» такого формата не принимается. Можно: ${listFormats(policy)}.`;
  }
  if (!(file.size > 0)) return `Файл «${name}» пустой.`;
  const limit = format.kind === 'image' ? policy.maxImageSizeBytes : policy.maxDocumentSizeBytes;
  if (file.size > limit) {
    return `Файл «${name}» слишком большой (${formatBytesRu(file.size)}). Не больше ${formatBytesRu(limit)}.`;
  }
  return null;
}

/**
 * Отказ, который композер показывает как есть: текст уже для человека.
 * Тред отличает его по `name`, а не по классу — модуль с `instanceof` через
 * границы бандлов ненадёжен.
 */
export class UploadRefusalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadRefusal';
  }
}

/** Код отказа сервера (ProblemDetails `code` / `detail`, статус) → слова. */
export function uploadRefusalText(err: unknown, fileName: string, policy?: ChatAttachmentPolicy | null): string {
  const e = (err ?? {}) as { code?: unknown; detail?: unknown; status?: unknown; maxBytes?: unknown };
  const code = String(e.code ?? e.detail ?? '').trim();
  const status = Number(e.status);
  const maxBytes = Number(e.maxBytes);
  const name = fileName || 'файл';
  switch (code) {
    case 'unsupported_format':
      return `Файл «${name}» такого формата не принимается.${policy ? ` Можно: ${listFormats(policy)}.` : ''}`;
    case 'too_large':
      return `Файл «${name}» слишком большой.${Number.isFinite(maxBytes) && maxBytes > 0 ? ` Не больше ${formatBytesRu(maxBytes)}.` : ''}`;
    case 'empty_file':
      return `Файл «${name}» пустой.`;
    case 'attachments_forbidden':
      return 'Прикреплять файлы в этом чате вам сейчас нельзя.';
    case 'attachments_disabled':
      return 'Вложения в этом чате отключены.';
    case 'channel_not_found':
      return 'Чат не найден. Обновите страницу.';
    case 'moderation_blocked':
      return `Файл «${name}» не прошёл проверку правил.`;
    case 'moderation_unavailable':
    case 'chat_unavailable':
      return 'Вложения сейчас недоступны. Попробуйте чуть позже.';
    default:
      break;
  }
  if (status === 413) return `Файл «${name}» слишком большой.`;
  if (status === 401) return 'Войдите, чтобы прикреплять файлы.';
  if (status === 403) return 'Прикреплять файлы в этом чате вам сейчас нельзя.';
  return `Не удалось загрузить «${name}». Проверьте связь и попробуйте ещё раз.`;
}

/** «PDF», «DOCX», «Изображение» — тип файла для подписи в пузыре. */
export function fileKindLabel(mimeType?: string | null, fileName?: string | null): string {
  const ext = fileName ? extensionOf(fileName) : '';
  if (ext) return ext.toUpperCase();
  const mime = (mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'Изображение';
  if (mime === 'application/pdf') return 'PDF';
  return 'Файл';
}

/**
 * Вложение из `attachmentsJson` сервера — `{ id, type, kind, fileName, title,
 * mimeType, fileSizeBytes, url }` — в то, что читает `AttachmentView`
 * (`image_url` / `asset_url`, `title`). `url` — путь от корня основного API с
 * подписью; срок её жизни сервер обновляет при каждом чтении истории.
 * Прежние дескрипторы (с `image_url` / `asset_url`) не трогаем.
 *
 * CORE-1739: сервер больше не кладёт `url` (пересланная ссылка открывала файл
 * кому угодно). Тогда вместо адреса — `link_url`: по нему тот, кто сейчас в
 * канале, берёт одноразовую ссылку на пару минут в момент показа.
 */
export function normalizeChatAttachment(a: any, apiBase: string): any {
  if (!a || typeof a !== 'object') return a;
  if (a.image_url || a.asset_url || !a.id) return a;
  if (a.url == null && (a.fileName || a.kind)) {
    const name = a.fileName ?? a.title ?? 'Файл';
    return {
      ...a,
      type: a.type === 'image' || a.kind === 'image' ? 'image' : 'file',
      title: name,
      file_name: name,
      mime_type: a.mimeType ?? a.mime_type,
      file_size: a.fileSizeBytes ?? a.file_size,
      link_url: `/api/chat/attachments/${encodeURIComponent(String(a.id))}/link`,
    };
  }
  if (typeof a.url !== 'string') return a;
  const url: string = a.url;
  const abs = url.startsWith('/') ? `${apiBase}${url}` : url;
  const name = a.fileName ?? a.title ?? 'Файл';
  const common = {
    ...a,
    title: name,
    file_name: name,
    mime_type: a.mimeType ?? a.mime_type,
    file_size: a.fileSizeBytes ?? a.file_size,
    download_url: `${abs}${abs.includes('?') ? '&' : '?'}download=true`,
  };
  return a.type === 'image' || a.kind === 'image'
    ? { ...common, type: 'image', image_url: abs }
    : { ...common, type: 'file', asset_url: abs };
}

/** Подпись файла в пузыре: «отчёт.pdf · PDF · 1,2 МБ». */
export function attachmentCaption(a: any): string {
  const name = a?.file_name ?? a?.title ?? a?.fileName ?? 'Файл';
  const parts = [String(name), fileKindLabel(a?.mime_type ?? a?.mimeType, name)];
  const size = Number(a?.file_size ?? a?.fileSizeBytes);
  if (Number.isFinite(size) && size > 0) parts.push(formatBytesRu(size));
  return parts.join(' · ');
}
